import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Optional, Output } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { InputValidationResult } from '@bl/components/dist/input/types';
import { PhoneInputProps } from '@bl/components/dist/types';
import { BLSvelteComponentWithValidation } from '@bl/components/dist/wrappers/wrappers';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { BlFrontendBaseComponent } from './bl-frontend-base.component';

const generateUniqueNameId = (() => {
  let id = 0;
  return () => {
    id += 1;
    return `bl-frontend-phone-input-name-${id}`;
  };
})();

@Component({
  selector: 'bl-frontend-phone-input',
  template: ``,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlFrontendPhoneInputComponent
  extends BlFrontendBaseComponent<PhoneInputProps>
  implements ControlValueAccessor
{
  @Input() value: PhoneInputProps['value'] = '';
  @Input() text: PhoneInputProps['text'] = '';
  @Input() name: PhoneInputProps['name'] = generateUniqueNameId();
  @Input() placeholder: PhoneInputProps['placeholder'] = '';
  @Input() maxlength: PhoneInputProps['maxlength'];
  @Input() disabled: PhoneInputProps['disabled'] = false;
  @Input() debounced: PhoneInputProps['debounced'] = null;
  @Input() description: PhoneInputProps['description'] = '';
  @Input() autocomplete?: PhoneInputProps['autocomplete'];
  @Input() onValidate: PhoneInputProps['onValidate'] = null;
  @Input() onBlur?: PhoneInputProps['onBlur'];
  @Input() onKeyDown?: PhoneInputProps['onKeyDown'];

  @Output() blurEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() keyDownEvent: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() validationChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private statusChangesSubscription: Subscription;
  constructor(
    protected override ref: ElementRef,
    @Optional() private ngControl: NgControl,
  ) {
    super(ref);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  protected override onInitialized = (): void => {
    this.statusChangesSubscription = this.ngControl?.statusChanges?.pipe(distinctUntilChanged()).subscribe(() => {
      (this.instance as BLSvelteComponentWithValidation<PhoneInputProps, unknown>).validate();
    });
  };

  protected override onDestroyed = (): void => {
    this.statusChangesSubscription?.unsubscribe();
  };

  private onChange: (value: unknown) => void;
  private onTouched: () => void;

  writeValue(value: PhoneInputProps['value']): void {
    this.value = value;
    this.updateProps({ value });
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.updateProps({ disabled: isDisabled });
  }

  protected getComponentName(): string {
    return 'PhoneInput';
  }

  private validateInput(value: string): InputValidationResult {
    if (!this.onValidate) {
      return { valid: true, message: '' };
    }

    return this.onValidate(value);
  }

  protected getProps(): PhoneInputProps {
    const props: PhoneInputProps = {
      value: this.value,
      debounced: this.debounced,
      description: this.description,
      disabled: this.disabled,
      maxlength: this.maxlength,
      name: this.name,
      placeholder: this.placeholder,
      text: this.text,
      autocomplete: this.autocomplete,

      onBlur: (value: string): void => {
        this.blurEvent.emit(value);
        this.onTouched?.();
      },
      onChange: (value: string): void => {
        this.value = value;
        this.valueChange.emit(value);
        this.onChange?.(value);
        const validationResult = this.validateInput(value);
        this.validationChange.emit(validationResult.valid);
      },
      onKeyDown: (e: KeyboardEvent): void => {
        this.keyDownEvent.emit(e);
      },
    };

    if (this.onValidate) {
      props.onValidate = this.validateInput.bind(this);
    }

    return props;
  }
}
